<script setup>
import {ref, computed, watch} from 'vue'
import SidebarToggle from '@/common/sidebar-toggle/SidebarToggle'
import SiteTreeNavigationMenu from '@/common/site-tree/SiteTreeNavigationMenu'
import SiteTree from '@/common/site-tree/SiteTree'
import FormNavAndForm from '@/common/form/FormNavAndForm'
import FormHeader from '@/common/form/FormHeader'
import {useFormsStore} from '@/stores/forms'
import {useFormStore} from '@/stores/form'
import {usePageContentDataStore} from '@/stores/pageContentData'
import {useItemDataStore} from '@/stores/itemData'
import {useSiteTreeStore} from '@/stores/siteTree'
import {useAppStore} from '@/stores/appStore'
import {useRouter, useRoute} from 'vue-router'

const router = useRouter()
const route = useRoute()

const appStore = useAppStore()
const formsStore = useFormsStore()
const pageContentDataStore = usePageContentDataStore()
const itemDataStore = useItemDataStore()

const formId = ref(undefined)
const tableName = ref(undefined)
const itemId = ref(undefined)
const siteTreeId = 'siteTree'
const siteTreeStore = useSiteTreeStore(siteTreeId)

const showSaveAndCloseBtn = computed(() => tableName.value === 'content')

const goTo = (id) => {
    router.push({name: 'siteTree', params: {pageOrContentId: id}})
}

const goToHome = () => {
    goTo(appStore.settings.homePage)
}

const onSave = (obj) => {
    if (tableName.value === 'pages' && siteTreeStore.selectedPageId !== obj.id) {
        goTo(obj.id)
    }
    siteTreeStore.init()
}

const createForm = () => {
    formId.value = `form:${tableName.value}:${itemId.value}`

    formsStore.createForm({
        tableName: tableName.value,
        id: itemId.value,
        onSave: onSave,
        onInit: () => {
            if (itemId.value === 0 && tableName.value === 'pages') {
                const lastId = siteTreeStore.lastSelectedPageId
                if (lastId) {
                    itemDataStore.get({tableName: 'pages', id: lastId})
                        .then((obj) => {
                            const formStore = useFormStore(formId.value)
                            formStore.presetData({name: 'navigation', value: obj.navigation})
                            formStore.presetData({name: 'parent', value: obj.parent})
                        })
                }
            }
        }
    })
}

const init = () => {
    let id = route.params.pageOrContentId

    if (id) {
        if (typeof id === 'string' && id.indexOf('c') === 0) {
            id = parseInt(id.substring(1))
            siteTreeStore.setContentId(id)
            tableName.value = 'content'

            if (id > 0) {
                const obj = pageContentDataStore.items.find(o => o.id === id)
                if (!obj) {
                    goToHome()
                    return
                }
                itemId.value = obj.contentId
                siteTreeStore.addExpandedPageId(obj.pageId)
                siteTreeStore.setExpandedPageIds(obj.pageId)
                createForm()
            } else {
                itemId.value = 0
                createForm()
            }
        } else {
            tableName.value = 'pages'
            id = parseInt(id)
            siteTreeStore.setPageId(id)
            siteTreeStore.setExpandedPageIds(id)
            itemId.value = id
            createForm()
        }
    } else {
        if (siteTreeStore.selectedPageId) {
            goTo(siteTreeStore.selectedPageId)
        } else if (siteTreeStore.selectedContentId) {
            goTo(`c${siteTreeStore.selectedContentId}`)
        } else {
            goToHome()
        }
    }
}

watch(
    () => route.params.pageOrContentId,
    (newVal, oldVal) => {
        if (newVal !== oldVal) {
            init()
        }
    }
)

appStore.navigationComponent = 'site_tree'

const parentChildIds = siteTreeStore.parentChildIds
if (Object.keys(parentChildIds).length === 0) {
    siteTreeStore.init({
        onPageSelect: (pId) => {
            siteTreeStore.setPageId(pId)
            goTo(pId)
        },
        onContentSelect: (cId) => {
            siteTreeStore.setContentId(cId)
            goTo(`c${cId}`)
        }
    })
}

// Initial run for the first load
init()
</script>

<template>
    <SidebarToggle :name="'site_tree'" class="d-flex h-100 overflow-auto">
        <template v-slot:leftSidebar>
            <div class="flex-grow-1">
                <div class="text-center">
                    <SiteTreeNavigationMenu :site-tree-id="siteTreeId" class="m-3 d-inline-block"/>
                </div>

                <SiteTree :site-tree-id="siteTreeId"/>
            </div>
        </template>

        <section class="d-flex h-100 overflow-auto flex-grow-1 flex-column">
            <FormHeader
                v-if="formId"
                :key="'header-' + formId"
                :form-id="formId"
                :show-add-btn="true"
                :show-save-and-close-btn="showSaveAndCloseBtn"
                :show-save-btn="true"
            />

            <FormNavAndForm
                v-if="formId"
                :key="'body-' + formId"
                :form-id="formId"
                :show-archive-btn="true"
                :show-duplicate-btn="true"
                :show-history-btn="true"
            />
        </section>
    </SidebarToggle>
</template>